import { useEffect, useRef, useState } from "react";
import { Banknote, CreditCard, Loader2, MoreHorizontal, QrCode, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { useAuth } from "@/context/AuthContext";
import { activeMethods, brl, PAY_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";

const ICONS = { dinheiro: Banknote, pix: QrCode, credito: CreditCard, debito: Wallet };

export function OrderPayDialog({ open, onClose, total, onConfirm }) {
  const { session } = useAuth();
  const methods = activeMethods(session);
  const [method, setMethod] = useState("dinheiro");
  const [change, setChange] = useState("");
  const [busy, setBusy] = useState(false);
  const cashRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setMethod(methods.includes("dinheiro") ? "dinheiro" : methods[0]);
    setChange("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeFor = parseMoney(change);
  const bad = method === "dinheiro" && changeFor > 0 && changeFor < total;
  const go = async () => {
    setBusy(true);
    try { await onConfirm({ payment_method: method, change_for: method === "dinheiro" ? changeFor : 0 }); } catch { /* erro já mostrado */ } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" onOpenAutoFocus={(e) => { if (cashRef.current) { e.preventDefault(); cashRef.current.focus(); } }} data-testid="order-pay-dialog">
        <DialogHeader>
          <DialogTitle>Pagamento do pedido</DialogTitle>
          <DialogDescription>Total a receber: <b className="font-mono text-foreground" data-testid="order-pay-total">{brl(total)}</b></DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {methods.map((m) => {
            const Icon = ICONS[m] || MoreHorizontal;
            return (
              <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`order-pay-method-${m}`}
                className={cn("flex h-16 flex-col items-center justify-center gap-1 rounded-xl border px-1 text-center text-xs font-semibold transition-colors", method === m ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent")}>
                <Icon className="h-5 w-5" />{PAY_LABELS[m]}
              </button>
            );
          })}
        </div>
        {method === "dinheiro" && (
          <div className="space-y-1">
            <label htmlFor="order-change-for" className="text-sm text-muted-foreground">Troco para quanto? (vazio = sem troco)</label>
            <MoneyInput ref={cashRef} id="order-change-for" autoFocus value={change} onChange={setChange} placeholder="Digite o valor" className="h-11 font-mono text-lg" data-testid="order-change-input" />
            {changeFor > total && <p className="text-sm font-semibold text-emerald-600" data-testid="order-change-value">Levar troco de {brl(changeFor - total)}</p>}
            {bad && <p className="text-sm text-red-500" data-testid="order-change-bad">O valor do troco precisa ser maior que o total.</p>}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="order-pay-back">Voltar</Button>
          <Button onClick={go} disabled={busy || bad} data-testid="order-pay-confirm-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Criar pedido · {brl(total)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
